<template>
  <div>
    <main-layout>
      <template #header>
        <main-header :title="'크레딧 관리' | translate" />
      </template>
      <template #feature>
        <div class="ai-feature-right">
          <v-btn
            class="mr-2"
            outlined
            color="primary"
            depressed
            @click="openGlobalPolicyDialog"
          >
            기본 정책 변경
          </v-btn>
          <v-btn color="primary" depressed @click="openNewTopupDialog">
            신규 업체 충전
          </v-btn>
        </div>
      </template>
      <template #toolbar>
        <main-toolbar>
          <search-bar
            v-model="searchOptions.searchKeyword"
            :placeholder="'업체명/업체번호 검색' | translate"
            style="width: 300px"
            @update:keyword="onKeywordChanged"
          />
          <v-text-field
            v-model.number="searchOptions.rentalcompanyId"
            :label="'임대업체번호' | translate"
            type="number"
            dense
            outlined
            hide-details
            clearable
            style="width: 160px"
          />
          <v-btn color="primary" class="ml-2" @click="onSearchClick">
검색
</v-btn>
        </main-toolbar>
      </template>

      <template #body>
        <main-body class="ai-credit-manage-body">
          <div
            ref="tableWrap"
            class="ai-credit-manage-table-wrap"
            :style="tableWrapStyle"
          >
            <vue-data-table
              ref="dataTable"
              :fetch="fetch"
              :headers="headers"
              item-key="raw.rentalcompanyId"
              scroll-always-visible
              infinite-pagination
              :items-per-page="30"
              @click:row="onClickRow"
            >
            <template #item.raw.subscriptionActiveYn="{ item }">
              <span>{{ formatSubscriptionActiveYn(item.raw.subscriptionActiveYn) }}</span>
            </template>
            <template #item.raw.dailyCreditLimit="{ item }">
              <div @click.stop>
                <div
                  v-if="canEditCompanyPolicy(item.raw) && isPolicyEditing(item.raw, 'dailyCreditLimit')"
                  class="ai-policy-edit-cell"
                >
                  <v-text-field
                    v-model.number="policyDraftFor(item.raw).dailyCreditLimit"
                    type="number"
                    min="0"
                    step="0.0001"
                    dense
                    hide-details
                    single-line
                    class="ai-policy-edit-cell__input"
                    autofocus
                  />
                  <v-btn
                    icon
                    x-small
                    :loading="isPolicyFieldSaving(item.raw, 'dailyCreditLimit')"
                    :disabled="!!policySavingKey && !isPolicyFieldSaving(item.raw, 'dailyCreditLimit')"
                    @click="savePolicyField(item.raw, 'dailyCreditLimit')"
                  >
                    <v-icon small>
                      mdi-content-save
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :disabled="!!policySavingKey"
                    @click="cancelPolicyEdit(item.raw, 'dailyCreditLimit')"
                  >
                    <v-icon small>
                      mdi-close
                    </v-icon>
                  </v-btn>
                </div>
                <span
                  v-else-if="canEditCompanyPolicy(item.raw)"
                  class="ai-policy-editable"
                  @click="startPolicyEdit(item.raw, 'dailyCreditLimit')"
                >{{ item.raw.dailyCreditLimit }}</span>
                <span v-else class="ai-policy-readonly">{{ item.raw.dailyCreditLimit }}</span>
              </div>
            </template>
            <template #item.raw.topupUnitCredit="{ item }">
              <div @click.stop>
                <div
                  v-if="canEditCompanyPolicy(item.raw) && isPolicyEditing(item.raw, 'topupUnitCredit')"
                  class="ai-policy-edit-cell"
                >
                  <v-text-field
                    v-model.number="policyDraftFor(item.raw).topupUnitCredit"
                    type="number"
                    min="0"
                    step="0.0001"
                    dense
                    hide-details
                    single-line
                    class="ai-policy-edit-cell__input"
                    autofocus
                  />
                  <v-btn
                    icon
                    x-small
                    :loading="isPolicyFieldSaving(item.raw, 'topupUnitCredit')"
                    :disabled="!!policySavingKey && !isPolicyFieldSaving(item.raw, 'topupUnitCredit')"
                    @click="savePolicyField(item.raw, 'topupUnitCredit')"
                  >
                    <v-icon small>
                      mdi-content-save
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :disabled="!!policySavingKey"
                    @click="cancelPolicyEdit(item.raw, 'topupUnitCredit')"
                  >
                    <v-icon small>
                      mdi-close
                    </v-icon>
                  </v-btn>
                </div>
                <span
                  v-else-if="canEditCompanyPolicy(item.raw)"
                  class="ai-policy-editable"
                  @click="startPolicyEdit(item.raw, 'topupUnitCredit')"
                >{{ item.raw.topupUnitCredit }}</span>
                <span v-else class="ai-policy-readonly">{{ item.raw.topupUnitCredit }}</span>
              </div>
            </template>
            <template #item.raw.subscriptionFeeMoney="{ item }">
              <div @click.stop>
                <div
                  v-if="canEditCompanyPolicy(item.raw) && isPolicyEditing(item.raw, 'subscriptionFeeMoney')"
                  class="ai-policy-edit-cell"
                >
                  <v-text-field
                    v-model.number="policyDraftFor(item.raw).subscriptionFeeMoney"
                    type="number"
                    min="0"
                    step="1"
                    dense
                    hide-details
                    single-line
                    class="ai-policy-edit-cell__input"
                    autofocus
                  />
                  <v-btn
                    icon
                    x-small
                    :loading="isPolicyFieldSaving(item.raw, 'subscriptionFeeMoney')"
                    :disabled="!!policySavingKey && !isPolicyFieldSaving(item.raw, 'subscriptionFeeMoney')"
                    @click="savePolicyField(item.raw, 'subscriptionFeeMoney')"
                  >
                    <v-icon small>
                      mdi-content-save
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :disabled="!!policySavingKey"
                    @click="cancelPolicyEdit(item.raw, 'subscriptionFeeMoney')"
                  >
                    <v-icon small>
                      mdi-close
                    </v-icon>
                  </v-btn>
                </div>
                <span
                  v-else-if="canEditCompanyPolicy(item.raw)"
                  class="ai-policy-editable"
                  @click="startPolicyEdit(item.raw, 'subscriptionFeeMoney')"
                >{{ item.raw.subscriptionFeeMoney }}</span>
                <span v-else class="ai-policy-readonly">{{ item.raw.subscriptionFeeMoney }}</span>
              </div>
            </template>
            <template #item.raw.effectiveStartAt="{ item }">
              <div class="ai-credit-cell-action" @click.stop>
                <span v-if="isActiveSubscription(item.raw)">
                  {{ formatKstDateTime(item.raw.effectiveStartAt) }}
                </span>
                <v-btn
                  v-else
                  x-small
                  depressed
                  color="primary"
                  @click="openTopupDialogForRow(item.raw)"
                >
                  구독 신청
                </v-btn>
              </div>
            </template>
            <template #item.raw.effectiveEndAt="{ item }">
              <div class="ai-credit-cell-action" @click.stop>
                <span v-if="item.raw.effectiveEndAt">
                  {{ formatKstDateTime(item.raw.effectiveEndAt) }}
                </span>
                <v-btn
                  v-else-if="isActiveSubscription(item.raw)"
                  x-small
                  depressed
                  color="error"
                  :loading="cancelLoadingRentalcompanyId === item.raw.rentalcompanyId"
                  @click="cancelSubscription(item.raw)"
                >
                  해지
                </v-btn>
                <span v-else>-</span>
              </div>
            </template>
          </vue-data-table>
          </div>
        </main-body>
      </template>
    </main-layout>

    <v-dialog v-model="topupDialog" max-width="460">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">
          크레딧 충전
        </v-card-title>
        <v-card-subtitle class="caption text--secondary">
          관리자 수동 충전으로 업체 크레딧 지갑에 즉시 반영됩니다.
        </v-card-subtitle>
        <v-divider />
        <v-card-text class="pt-4">
          <search-select-auto-complete
            v-if="topupFormMode === 'new'"
            v-model="topupForm.rentalcompanyId"
            :items="companyOptions"
            label="업체"
            class="mb-2"
            hide-details
          />
          <v-text-field
            v-else
            v-model.number="topupForm.rentalcompanyId"
            label="임대업체번호"
            dense
            outlined
            hide-details
            class="mb-2"
            readonly
          />
          <v-text-field
            v-model.number="topupForm.topupCredit"
            label="충전 크레딧"
            type="number"
            min="0"
            step="0.0001"
            dense
            outlined
            hide-details
            class="mb-2"
          />
          <v-text-field
            v-model.number="topupForm.amountMoney"
            label="결제 금액(원)"
            type="number"
            min="0"
            step="0.01"
            dense
            outlined
            hide-details
            class="mb-2"
          />
          <v-text-field
            v-model="topupForm.memo"
            label="메모"
            dense
            outlined
            hide-details
            placeholder="관리자 수동 충전"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-btn text :disabled="topupLoading" @click="topupDialog = false">
            닫기
          </v-btn>
          <v-spacer />
          <v-btn depressed color="primary" :loading="topupLoading" @click="submitTopup">
            충전
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="globalPolicyDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">
          기본 정책 변경 (GLOBAL)
        </v-card-title>
        <v-card-subtitle class="caption text--secondary ai-global-policy-subtitle">
          구독하지 않는 업체 또는 신규 구독에 적용될 값이며 기존 구독 업체의 정책은 변경되지 않습니다.
        </v-card-subtitle>
        <v-divider />
        <v-card-text v-if="globalPolicyLoading" class="py-8 text-center">
          <v-progress-circular indeterminate color="primary" size="32" />
        </v-card-text>
        <v-card-text v-else class="pt-4">
          <v-text-field
            v-model.number="globalPolicyForm.dailyCreditLimit"
            label="일 크레딧 한도"
            type="number"
            min="0"
            step="0.0001"
            dense
            outlined
            hide-details
            class="mb-3"
          />
          <v-text-field
            v-model.number="globalPolicyForm.topupUnitCredit"
            label="정책 충전단위"
            type="number"
            min="0"
            step="0.0001"
            dense
            outlined
            hide-details
            class="mb-3"
          />
          <v-text-field
            v-model.number="globalPolicyForm.subscriptionFeeMoney"
            label="정책 구독료(원)"
            type="number"
            min="0"
            step="1"
            dense
            outlined
            hide-details
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-btn text :disabled="globalPolicySaving" @click="globalPolicyDialog = false">
            닫기
          </v-btn>
          <v-spacer />
          <v-btn
            depressed
            color="primary"
            :loading="globalPolicySaving"
            :disabled="globalPolicyLoading"
            @click="submitGlobalPolicy"
          >
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="ledgerDialog" max-width="1120">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">
          크레딧 원장 이력
        </v-card-title>
        <v-card-subtitle class="caption text--secondary">
          {{ ledgerDialogTitle }}
        </v-card-subtitle>
        <v-divider />
        <v-card-text class="pt-4">
          <v-data-table
            :headers="ledgerHeaders"
            :items="ledgerItems"
            :loading="ledgerLoading"
            dense
            fixed-header
            height="520"
            item-key="id"
            :items-per-page="100"
          >
            <template #item.createdAt="{ item }">
              {{ formatKstDateTime(item.createdAt) }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn text @click="ledgerDialog = false">
닫기
</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { PATH } from "@/constants";
import MainLayout from "@/components/common/layout/MainLayout";
import MainHeader from "@/components/common/layout/MainHeader";
import MainToolbar from "@/components/common/layout/MainToolbar";
import MainBody from "@/components/common/layout/MainBody";
import SearchBar from "@/components/input/SearchBar";
import VueDataTable from "@/assets/plugins/vue-data-table/VueDataTable";
import Board from "@/model/board";
import SearchSelectAutoComplete from "@/components/selectBox/SearchSelectAutoComplete";
import TotalCodeOptions from "@/assets/options/TotalCodeOptions";
import { dateHelper } from "@/assets/plugins/mps-common/time/date-helper";

export default {
  name: "AiCreditManage",
  components: {
    MainLayout,
    MainHeader,
    MainToolbar,
    MainBody,
    SearchBar,
    VueDataTable,
    SearchSelectAutoComplete,
  },
  mixins: [TotalCodeOptions],
  data() {
    return {
      headers: [
        { text: "업체번호", value: "raw.rentalcompanyId", width: 110 },
        { text: "업체명", value: "raw.rentalcompanyName", width: 260 },
        { text: "잔여 크레딧", value: "raw.balanceCredit", width: 140 },
        { text: "일 크레딧 한도", value: "raw.dailyCreditLimit", width: 168, sortable: false },
        { text: "정책 충전단위", value: "raw.topupUnitCredit", width: 168, sortable: false },
        { text: "정책 구독료(원)", value: "raw.subscriptionFeeMoney", width: 168, sortable: false },
        {
          text: "구독",
          value: "raw.subscriptionActiveYn",
          width: 90,
          sortable: false,
          read: (v) => (v === "Y" ? "구독중" : v === "N" ? "해지" : "-"),
        },
        {
          text: "첫충전 일시",
          value: "raw.effectiveStartAt",
          width: 180,
          sortable: false,
          read: (v) => (v ? dateHelper.formatAdminDate(v) : "-"),
        },
        {
          text: "해지일시",
          value: "raw.effectiveEndAt",
          width: 180,
          sortable: false,
          read: (v) => (v ? dateHelper.formatAdminDate(v) : "-"),
        },
        { text: "마지막 충전월", value: "raw.lastRechargedMonth", width: 140 },
        {
          text: "최근 충전시각",
          value: "raw.lastTopupAt",
          width: 170,
          read: (v) => (v ? dateHelper.formatAdminDate(v) : "-"),
        },
        { text: "최근 충전크레딧", value: "raw.lastTopupCredit", width: 140 },
        { text: "최근 충전금액", value: "raw.lastTopupAmountMoney", width: 140 },
        {
          text: "지갑수정시각",
          value: "raw.walletUpdatedAt",
          width: 170,
          read: (v) => (v ? dateHelper.formatAdminDate(v) : "-"),
        },
      ],
      searchOptions: {
        searchKeyword: null,
        rentalcompanyId: null,
      },
      topupDialog: false,
      topupLoading: false,
      cancelLoadingRentalcompanyId: null,
      topupFormMode: "row",
      topupForm: {
        rentalcompanyId: null,
        rentalcompanyName: "",
        topupCredit: 1000,
        amountMoney: 9900,
        memo: "관리자 수동 충전",
      },
      tableWrapHeightPx: null,
      policyDrafts: {},
      policyEditingKey: null,
      policySavingKey: null,
      globalPolicyDialog: false,
      globalPolicyLoading: false,
      globalPolicySaving: false,
      globalPolicyForm: {
        dailyCreditLimit: null,
        topupUnitCredit: null,
        subscriptionFeeMoney: null,
      },
      policyFieldLabels: {
        dailyCreditLimit: "일 크레딧 한도",
        topupUnitCredit: "정책 충전단위",
        subscriptionFeeMoney: "정책 구독료(원)",
      },
      ledgerDialog: false,
      ledgerLoading: false,
      ledgerItems: [],
      ledgerTarget: null,
      ledgerHeaders: [
        { text: "ID", value: "id", width: 80 },
        { text: "일시", value: "createdAt", width: 160 },
        { text: "구분", value: "entryType", width: 100 },
        { text: "방향", value: "direction", width: 80 },
        { text: "크레딧", value: "amountCredit", width: 120 },
        { text: "잔액", value: "balanceAfterCredit", width: 120 },
        { text: "참조", value: "refType", width: 120 },
        { text: "참조ID", value: "refId", width: 100 },
        { text: "요청키", value: "requestKey", width: 240 },
        { text: "메모", value: "memo", width: 260 },
        { text: "처리자", value: "createdBy", width: 100 },
      ],
    };
  },
  computed: {
    tableWrapStyle() {
      if (this.tableWrapHeightPx == null) {
        return {};
      }
      const h = `${this.tableWrapHeightPx}px`;
      return { height: h, maxHeight: h };
    },
    ledgerDialogTitle() {
      if (!this.ledgerTarget) return "";
      const name = this.ledgerTarget.rentalcompanyName || "";
      return `${this.ledgerTarget.rentalcompanyId || ""} ${name}`.trim();
    },
  },
  watch: {
    "topupForm.rentalcompanyId"(newValue) {
      if (this.topupFormMode !== "new") return;
      const selected = (this.companyOptions || []).find(
        (item) => String(item.codeKey) === String(newValue)
      );
      if (selected) {
        this.topupForm.rentalcompanyName = selected.codeValue || "";
      }
    },
  },
  created() {
    this.loadCompanyOptions();
  },
  mounted() {
    this.syncTableWrapHeight();
    window.addEventListener("resize", this.syncTableWrapHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.syncTableWrapHeight);
  },
  methods: {
    syncTableWrapHeight() {
      this.$nextTick(() => {
        const el = this.$refs.tableWrap;
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        const height = Math.floor(window.innerHeight * 0.95 - top);
        this.tableWrapHeightPx = Math.max(240, height);
      });
    },
    formatKstDateTime(value) {
      if (!value) return "-";
      const formatted = dateHelper.formatAdminDate(value);
      return formatted || "-";
    },
    formatSubscriptionActiveYn(value) {
      if (value === "Y") return "구독중";
      if (value === "N") return "해지";
      return "-";
    },
    async fetch(pageNumber, pageSize) {
      const res = await this.$request(PATH.AI_MANAGE.CREDIT_WALLET_LIST)
        .post()
        .setObject({
          ...this.searchOptions,
          pageNumber,
          pageSize,
        })
        .catch()
        .enqueue();
      return (res.data || []).map((e) => new Board(e));
    },
    onKeywordChanged() {},
    onSearchClick() {
      this.policyDrafts = {};
      this.policyEditingKey = null;
      const table = this.$refs.dataTable;
      if (table && typeof table.clear === "function") {
        table.clear();
      }
    },
    onClickRow(item) {
      const row = item && item.raw ? item.raw : {};
      this.openLedgerDialog(row);
    },
    isActiveSubscription(row) {
      return !!(row && String(row.subscriptionActiveYn || "").toUpperCase() === "Y");
    },
    canEditCompanyPolicy(row) {
      return !!(row && String(row.hasCompanyPolicyYn || "").toUpperCase() === "Y");
    },
    policyCellKey(row, fieldKey) {
      if (!row || !row.rentalcompanyId) return null;
      return `${row.rentalcompanyId}:${fieldKey}`;
    },
    isPolicyEditing(row, fieldKey) {
      return this.policyEditingKey === this.policyCellKey(row, fieldKey);
    },
    startPolicyEdit(row, fieldKey) {
      if (!this.canEditCompanyPolicy(row) || this.policySavingKey) return;
      const draft = this.policyDraftFor(row);
      draft[fieldKey] = row[fieldKey];
      this.policyEditingKey = this.policyCellKey(row, fieldKey);
    },
    cancelPolicyEdit(row, fieldKey) {
      const key = this.policyCellKey(row, fieldKey);
      if (this.policyEditingKey !== key) return;
      const draft = this.policyDraftFor(row);
      draft[fieldKey] = row[fieldKey];
      this.policyEditingKey = null;
    },
    policyDraftFor(row) {
      if (!row || !row.rentalcompanyId) {
        return {
          dailyCreditLimit: null,
          topupUnitCredit: null,
          subscriptionFeeMoney: null,
        };
      }
      const id = row.rentalcompanyId;
      if (!this.policyDrafts[id]) {
        this.$set(this.policyDrafts, id, {
          dailyCreditLimit: row.dailyCreditLimit,
          topupUnitCredit: row.topupUnitCredit,
          subscriptionFeeMoney: row.subscriptionFeeMoney,
        });
      }
      return this.policyDrafts[id];
    },
    isPolicyFieldSaving(row, fieldKey) {
      if (!row || !row.rentalcompanyId) return false;
      return this.policySavingKey === `${row.rentalcompanyId}:${fieldKey}`;
    },
    async savePolicyField(row, fieldKey) {
      if (!row || !row.rentalcompanyId || this.policySavingKey) return;
      if (!this.canEditCompanyPolicy(row)) return;
      const label = this.policyFieldLabels[fieldKey] || fieldKey;
      const draft = this.policyDraftFor(row);
      const value = Number(draft[fieldKey]);
      if (Number.isNaN(value)) {
        return;
      }
      if (fieldKey === "topupUnitCredit" && value <= 0) {
        return;
      }
      if (fieldKey !== "topupUnitCredit" && value < 0) {
        return;
      }
      const ok = window.confirm(`${label} 변경하시겠습니까?`);
      if (!ok) return;

      const savingKey = `${row.rentalcompanyId}:${fieldKey}`;
      this.policySavingKey = savingKey;
      try {
        await this.$request(PATH.AI_MANAGE.CREDIT_POLICY_FIELD_UPDATE)
          .post()
          .setObject({
            rentalcompanyId: Number(row.rentalcompanyId),
            fieldKey,
            value,
          })
          .catch()
          .enqueue();
        this.$delete(this.policyDrafts, row.rentalcompanyId);
        if (this.policyEditingKey === savingKey) {
          this.policyEditingKey = null;
        }
        this.onSearchClick();
      } catch (e) {
        console.warn("ai credit policy field update", e);
      } finally {
        if (this.policySavingKey === savingKey) {
          this.policySavingKey = null;
        }
      }
    },
    openTopupDialogForRow(row) {
      this.topupFormMode = "row";
      this.topupForm = {
        rentalcompanyId: row.rentalcompanyId,
        rentalcompanyName: row.rentalcompanyName || "",
        topupCredit: Number(row.topupUnitCredit || 1000),
        amountMoney: Number(
          row.subscriptionFeeMoney || row.lastTopupAmountMoney || 9900
        ),
        memo: "구독 신청",
      };
      this.topupDialog = true;
    },
    openNewTopupDialog() {
      this.topupFormMode = "new";
      this.topupForm = {
        rentalcompanyId: null,
        rentalcompanyName: "",
        topupCredit: 1000,
        amountMoney: 9900,
        memo: "관리자 수동 충전",
      };
      this.topupDialog = true;
    },
    async openGlobalPolicyDialog() {
      this.globalPolicyDialog = true;
      await this.loadGlobalPolicyForm();
    },
    async loadGlobalPolicyForm() {
      this.globalPolicyLoading = true;
      try {
        const res = await this.$request(PATH.AI_MANAGE.CREDIT_GLOBAL_POLICY_DETAIL)
          .post()
          .setObject({})
          .catch()
          .enqueue();
        const data = res && res.data != null ? res.data : res;
        this.globalPolicyForm = {
          dailyCreditLimit: data.dailyCreditLimit,
          topupUnitCredit: data.topupUnitCredit,
          subscriptionFeeMoney: data.subscriptionFeeMoney,
        };
      } catch (e) {
        console.warn("ai credit global policy detail", e);
        this.globalPolicyDialog = false;
      } finally {
        this.globalPolicyLoading = false;
      }
    },
    validateGlobalPolicyForm() {
      const daily = Number(this.globalPolicyForm.dailyCreditLimit);
      const topup = Number(this.globalPolicyForm.topupUnitCredit);
      const fee = Number(this.globalPolicyForm.subscriptionFeeMoney);
      if (Number.isNaN(daily) || daily < 0) return false;
      if (Number.isNaN(topup) || topup <= 0) return false;
      if (Number.isNaN(fee) || fee < 0) return false;
      return true;
    },
    async submitGlobalPolicy() {
      if (this.globalPolicySaving || this.globalPolicyLoading) return;
      if (!this.validateGlobalPolicyForm()) return;
      const ok = window.confirm(
        "기본 정책(일 크레딧 한도, 정책 충전단위, 정책 구독료)을 변경하시겠습니까?"
      );
      if (!ok) return;

      this.globalPolicySaving = true;
      try {
        await this.$request(PATH.AI_MANAGE.CREDIT_GLOBAL_POLICY_UPDATE)
          .post()
          .setObject({
            dailyCreditLimit: Number(this.globalPolicyForm.dailyCreditLimit),
            topupUnitCredit: Number(this.globalPolicyForm.topupUnitCredit),
            subscriptionFeeMoney: Number(this.globalPolicyForm.subscriptionFeeMoney),
          })
          .catch()
          .enqueue();
        await this.loadGlobalPolicyForm();
        this.policyDrafts = {};
        this.policyEditingKey = null;
        this.onSearchClick();
        this.globalPolicyDialog = false;
      } catch (e) {
        console.warn("ai credit global policy update", e);
      } finally {
        this.globalPolicySaving = false;
      }
    },
    async openLedgerDialog(row) {
      if (!row || !row.rentalcompanyId) return;
      this.ledgerTarget = row;
      this.ledgerItems = [];
      this.ledgerDialog = true;
      this.ledgerLoading = true;
      try {
        const res = await this.$request(PATH.AI_MANAGE.CREDIT_LEDGER_LIST)
          .post()
          .setObject({
            rentalcompanyId: Number(row.rentalcompanyId),
            limit: 100,
          })
          .catch()
          .enqueue();
        this.ledgerItems = Array.isArray(res && res.data) ? res.data : [];
      } catch (e) {
        console.warn("ai credit ledger list", e);
      } finally {
        this.ledgerLoading = false;
      }
    },
    async cancelSubscription(row) {
      if (!row || !row.rentalcompanyId) return;
      if (this.cancelLoadingRentalcompanyId) return;
      const ok = window.confirm("해당 업체의 AI 크레딧 구독을 해지하시겠습니까?");
      if (!ok) return;
      this.cancelLoadingRentalcompanyId = row.rentalcompanyId;
      try {
        await this.$request(PATH.AI_MANAGE.CREDIT_SUBSCRIPTION_CANCEL)
          .post()
          .setObject({
            rentalcompanyId: Number(row.rentalcompanyId),
          })
          .catch()
          .enqueue();
        this.onSearchClick();
      } catch (e) {
        console.warn("ai credit subscription cancel", e);
      } finally {
        this.cancelLoadingRentalcompanyId = null;
      }
    },
    async submitTopup() {
      if (this.topupLoading) return;
      if (!this.topupForm.rentalcompanyId || Number(this.topupForm.rentalcompanyId) <= 0) {
        return;
      }
      if (!this.topupForm.topupCredit || Number(this.topupForm.topupCredit) <= 0) {
        return;
      }

      this.topupLoading = true;
      try {
        await this.$request(PATH.AI_MANAGE.CREDIT_TOPUP)
          .post()
          .setObject({
            rentalcompanyId: Number(this.topupForm.rentalcompanyId),
            topupCredit: Number(this.topupForm.topupCredit),
            amountMoney: Number(this.topupForm.amountMoney || 0),
            memo: this.topupForm.memo,
          })
          .catch()
          .enqueue();
        this.topupDialog = false;
        this.onSearchClick();
      } catch (e) {
        console.warn("ai credit topup", e);
      } finally {
        this.topupLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.ai-credit-manage-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.ai-credit-manage-table-wrap {
  min-height: 0;
  overflow: hidden;
}

.ai-credit-manage-table-wrap >>> .vue-data-table {
  height: 100%;
}

.ai-feature-right {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.ai-global-policy-subtitle {
  text-align: left;
  white-space: normal;
  line-height: 1.45;
  padding-top: 4px;
  padding-bottom: 8px;
  max-width: 100%;
}

.ai-global-policy-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.ai-global-policy-row__input {
  flex: 1 1 auto;
  margin-right: 8px;
}

.ai-global-policy-row.mb-0 {
  margin-bottom: 0;
}

.ai-credit-cell-action {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-policy-readonly {
  display: block;
  width: 100%;
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
  padding: 8px 4px;
}

.ai-policy-editable {
  display: block;
  width: 100%;
  text-align: center;
  font-size: 13px;
  padding: 8px 4px;
  cursor: pointer;
  border-radius: 4px;
}

.ai-policy-editable:hover {
  background: rgba(25, 118, 210, 0.08);
}

.ai-policy-edit-cell {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 2px 4px;
  box-sizing: border-box;
}

.ai-policy-edit-cell__input {
  flex: 1 1 auto;
  min-width: 0;
  margin-right: 2px;
}

.ai-policy-edit-cell__input >>> .v-input__control {
  min-height: 32px !important;
}

.ai-policy-edit-cell__input >>> input {
  font-size: 13px;
  padding: 0 4px;
}
</style>
